import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { resolveApiBase, diagnoseApiBase } from '@/lib/apiBase';

// Auto-healing API base. If the production bundle was built with a
// stale REACT_APP_BACKEND_URL pointing at a preview backend, this falls
// back to same-origin so the verify request actually reaches the
// production backend that issued the token.
const API = resolveApiBase();

// Hard ceilings so the spinner can never spin forever.
// Network request timeout (axios `timeout`): 12s. After this the request is aborted.
const VERIFY_REQUEST_TIMEOUT_MS = 12000;
// Wall-clock fallback that ALSO covers the case where the request is
// pending but the response never resolves for any reason: 15s.
const VERIFY_WALL_CLOCK_TIMEOUT_MS = 15000;
// How long to show the success screen before auto-redirecting to dashboard.
const AUTO_REDIRECT_MS = 1500;

/**
 * Email Verification Landing Page
 * Hits GET /api/auth/verify-email?token=... and renders the result.
 *
 * 2026-05-23 — spinner-stuck fix:
 *   - Added a hard `axios timeout` so the verify request can't hang forever
 *     on a slow backend cold-start (the most common cause of the long
 *     spinner reported by beta testers).
 *   - Added a wall-clock fallback (setTimeout in the effect) so even if
 *     the axios timeout itself somehow misses, the user lands on a clean
 *     error screen with a clear next step rather than an infinite spinner.
 *   - On success, we call `refreshUser()` so the in-memory auth state
 *     (and the EmailVerificationBanner that reads `user.email_verified`)
 *     updates without requiring a manual reload.
 *   - On success, we ALSO auto-redirect to /dashboard after 1.5s so the
 *     user doesn't have to tap "Continue" manually. The button stays
 *     available as a fallback for keyboard / accessibility users.
 *   - REMOVED the previous `useRef calledRef` StrictMode guard. Under
 *     React 18 StrictMode dev the guard combined with the cleanup-cancel
 *     pattern left the UI permanently in 'loading' state (the first
 *     effect's state writes get cancelled, and the second effect bails
 *     out at the ref check before setting up any new state writes).
 *     The backend endpoint is idempotent — a duplicate fire in dev is
 *     harmless and infinitely preferable to a stuck spinner.
 */
const VerifyEmail = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  // Diagnostic context — captured on every attempt so the error UI can
  // show the user (and us) exactly which backend the click landed on.
  // This was added after a 2026-06-17 production bug where every verify
  // link failed with "Invalid or already-used" because the frontend was
  // calling a different backend than the one that issued the token.
  const [diag, setDiag] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('This verification link is missing its token.');
      return;
    }

    let cancelled = false;
    let autoRedirectTimer = null;

    // Wall-clock guard — if neither the request nor the axios timeout
    // resolves within VERIFY_WALL_CLOCK_TIMEOUT_MS, force the UI out of
    // the loading state so the user is never stuck on a spinner.
    const wallClockTimer = setTimeout(() => {
      if (cancelled) return;
      setStatus((prev) => {
        if (prev === 'loading') {
          setMessage('Verification is taking longer than expected. Please try again or sign in to request a new link.');
          return 'error';
        }
        return prev;
      });
    }, VERIFY_WALL_CLOCK_TIMEOUT_MS);

    (async () => {
      // Capture diagnostic context up-front so it's available regardless of
      // request outcome — these are the EXACT values needed to debug a
      // verify-link mismatch in production (the backend host the call
      // hit, the page origin, the token length/prefix).
      const baseDiag = diagnoseApiBase();
      const _diag = {
        ...baseDiag,
        api_base: API || '(empty)',
        request_url: `${API}/api/auth/verify-email`,
        token_length: token ? token.length : 0,
        token_prefix: token ? token.slice(0, 6) + '…' : '(none)',
        page_origin: typeof window !== 'undefined' ? window.location.origin : null,
        page_href: typeof window !== 'undefined' ? window.location.href : null,
      };
      setDiag(_diag);

      try {
        const res = await axios.get(`${API}/api/auth/verify-email`, {
          params: { token },
          timeout: VERIFY_REQUEST_TIMEOUT_MS,
        });
        if (cancelled) return;
        if (res.data?.success) {
          setStatus('success');
          setMessage(res.data.message || 'Your email has been verified.');
          // Refresh the auth context so the banner / gated UI updates
          // without the user having to reload. Best-effort: a refresh
          // failure here MUST NOT block the success screen or the
          // auto-redirect — the verification itself already succeeded.
          try { await refreshUser?.(); } catch (_) { /* non-fatal */ }
          // Auto-redirect to dashboard so the user isn't stuck on the
          // "Continue to dashboard" button. They can still tap it
          // immediately if they want a faster transition.
          autoRedirectTimer = setTimeout(() => {
            if (!cancelled) navigate('/dashboard', { replace: true });
          }, AUTO_REDIRECT_MS);
        } else {
          setStatus('error');
          setMessage('Verification failed. Please try again.');
        }
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        const isTimeout = e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '');
        setMessage(
          isTimeout
            ? 'Verification is taking longer than expected. Please try again or sign in to request a new link.'
            : (e.response?.data?.detail ||
                'This link is invalid or has expired. Please request a new one.'),
        );
        // Pull whoami diagnostic in parallel — confirms which backend the
        // failure was served by. Best-effort, never blocks the error UI.
        try {
          const w = await axios.get(`${API}/api/_internal/whoami`, { timeout: 5000 });
          setDiag((d) => ({
            ...(d || {}),
            served_by_host: w.data?.served_by_host,
            backend_canonical_frontend_url: w.data?.backend_canonical_frontend_url,
            frontend_url_env_is_preview: w.data?.frontend_url_env_is_preview,
            response_status: e?.response?.status || null,
          }));
        } catch (_) { /* non-fatal */ }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(wallClockTimer);
      if (autoRedirectTimer) clearTimeout(autoRedirectTimer);
    };
  }, [token, navigate, refreshUser]);

  return (
    <div
      data-testid="verify-email-page"
      className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-4"
    >
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8">
          <div className="text-center space-y-4">
            {status === 'loading' && (
              <>
                <div className="flex justify-center">
                  <Loader2 className="h-12 w-12 text-emerald-600 animate-spin" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Verifying your email…
                </h2>
                <p className="text-gray-600">Just a moment.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="flex justify-center">
                  <div className="bg-emerald-100 rounded-full p-4">
                    <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Email verified
                </h2>
                <p className="text-gray-600">{message}</p>
                <p className="text-xs text-gray-500">
                  Redirecting you to your dashboard…
                </p>
                <Button
                  data-testid="verify-email-continue-btn"
                  onClick={() => navigate('/dashboard', { replace: true })}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  Continue to dashboard
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="flex justify-center">
                  <div className="bg-red-100 rounded-full p-4">
                    <AlertCircle className="h-12 w-12 text-red-600" />
                  </div>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Verification link not valid
                </h2>
                <p className="text-gray-600">{message}</p>
                <div className="space-y-2 pt-2">
                  <Button
                    data-testid="verify-email-login-btn"
                    onClick={() => navigate('/login')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    Go to login
                  </Button>
                  <p className="text-xs text-gray-500">
                    Once logged in you can request a new verification email
                    from your dashboard.
                  </p>
                </div>
                {diag && (
                  <details
                    data-testid="verify-email-diagnostic-panel"
                    className="text-left text-xs text-gray-500 bg-gray-50 rounded-md p-3 mt-3 border border-gray-200"
                  >
                    <summary className="cursor-pointer font-medium text-gray-700">
                      Show diagnostic info
                    </summary>
                    <dl className="mt-2 space-y-1 font-mono break-all">
                      <div><dt className="inline text-gray-500">API base (resolved):</dt> <dd className="inline">{diag.resolved || diag.api_base}</dd></div>
                      <div><dt className="inline text-gray-500">API base (build-time configured):</dt> <dd className="inline">{diag.configured || '(empty)'}</dd></div>
                      {diag.auto_healed_to_same_origin && (
                        <div className="bg-amber-50 border border-amber-200 rounded px-2 py-1 my-1">
                          <dt className="inline font-semibold text-amber-800">⚠ Auto-healed:</dt>
                          <dd className="inline ml-1 text-amber-800">
                            Build-time REACT_APP_BACKEND_URL pointed at a preview backend.
                            The app re-routed to same-origin to avoid breaking this request.
                            Please update REACT_APP_BACKEND_URL in your production frontend env.
                          </dd>
                        </div>
                      )}
                      <div><dt className="inline text-gray-500">Request URL:</dt> <dd className="inline">{diag.request_url}</dd></div>
                      <div><dt className="inline text-gray-500">Token length:</dt> <dd className="inline">{diag.token_length}</dd></div>
                      <div><dt className="inline text-gray-500">Token prefix:</dt> <dd className="inline">{diag.token_prefix}</dd></div>
                      <div><dt className="inline text-gray-500">Page origin:</dt> <dd className="inline">{diag.page_origin}</dd></div>
                      {diag.served_by_host && (
                        <div><dt className="inline text-gray-500">Backend host that served the failure:</dt> <dd className="inline">{diag.served_by_host}</dd></div>
                      )}
                      {diag.backend_canonical_frontend_url && (
                        <div><dt className="inline text-gray-500">Backend&apos;s canonical frontend URL:</dt> <dd className="inline">{diag.backend_canonical_frontend_url}</dd></div>
                      )}
                      {typeof diag.frontend_url_env_is_preview === 'boolean' && (
                        <div><dt className="inline text-gray-500">FRONTEND_URL is preview:</dt> <dd className="inline">{String(diag.frontend_url_env_is_preview)}</dd></div>
                      )}
                      {diag.response_status && (
                        <div><dt className="inline text-gray-500">HTTP status:</dt> <dd className="inline">{diag.response_status}</dd></div>
                      )}
                    </dl>
                    <p className="mt-2 text-[11px] text-gray-500">
                      Share this panel with support — it tells us whether the click hit the right backend.
                    </p>
                  </details>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
